import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  salesApi,
  type ProfileRow,
  type ReconciliationRow,
  type ResolutionMatch,
  type ResolutionRule,
  type ReviewRuleInfo,
  type RuleTreatment,
  type SalesChannelStatus,
  type SalesPaymentState,
} from '../lib/salesApi';

/**
 * Admin > Sales data > Check history: whether imported history can be
 * trusted before the dashboard shows it.
 *
 * The profile breaks a channel's orders down by year, status and payment
 * state. It exists because the websites count payments received: if a year's
 * orders were completed without their payments ever being entered, that year
 * reads as no sales at all. A cell like that is decided here, once, with a
 * saved rule, rather than order by order.
 */

const usd = (minor: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(minor / 100);

/** To the cent, where a one-cent difference is the whole point. */
const usdExact = (minor: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(minor / 100);

const PAYMENT_LABELS: Record<SalesPaymentState, string> = {
  none: 'No payment',
  partial: 'Part paid',
  full: 'Paid in full',
  over: 'Overpaid',
};

const TREATMENT_LABELS: Record<RuleTreatment, string> = {
  count_payments: 'Count the payments received',
  count_order_total: 'Count the order total',
  exclude: 'Exclude from sales',
};

export function AdminSalesHistory({ channels }: { channels: SalesChannelStatus[] }) {
  const withOrders = channels.filter((c) => c.orders.total > 0);
  const [channelId, setChannelId] = useState<string>(withOrders[0]?.channelId ?? '');

  useEffect(() => {
    if (!withOrders.some((c) => c.channelId === channelId)) setChannelId(withOrders[0]?.channelId ?? '');
  }, [withOrders, channelId]);

  const channel = withOrders.find((c) => c.channelId === channelId);

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>Check imported history</h3>
      {withOrders.length === 0 ? (
        <p className="muted small">Nothing has been imported yet. Once a channel has orders, its history can be checked here.</p>
      ) : (
        <>
          <div className="field" style={{ maxWidth: 320 }}>
            <label htmlFor="history-channel">Channel</label>
            <select id="history-channel" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
              {withOrders.map((c) => (
                <option key={c.channelId} value={c.channelId}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          {channel && <ChannelHistory key={channel.channelId} channel={channel} />}
        </>
      )}
      <ResolutionRules channels={channels} />
    </div>
  );
}

function ChannelHistory({ channel }: { channel: SalesChannelStatus }) {
  const profile = useQuery({
    queryKey: ['sales-profile', channel.channelId],
    queryFn: () => salesApi.recognitionProfile(channel.channelId),
  });
  const [deciding, setDeciding] = useState<ProfileRow | null>(null);
  const rulesById = useMemo(
    () => new Map((profile.data?.rules ?? []).map((r) => [r.id, r])),
    [profile.data?.rules],
  );

  if (profile.isLoading) return <div className="muted small">Loading…</div>;
  if (profile.error) return <div className="alert error">{(profile.error as Error).message}</div>;
  const rows = profile.data?.rows ?? [];

  return (
    <>
      <p className="small muted">
        {channel.capabilities.recognitionBasis === 'payments'
          ? 'Sales here count payments received, not the order status. A year of completed orders with no payment ' +
            'recorded usually means payments were never entered, not that nothing sold — decide those cells with a rule.'
          : 'Sales here count the order total the channel reports.'}
      </p>
      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>Year</th>
              <th>Status</th>
              <th>Payment</th>
              <th className="align-right num">Orders</th>
              <th className="align-right num">Order totals</th>
              <th className="align-right num">Received</th>
              <th className="align-right num">Counted as sales</th>
              <th>Needs review</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const open = row.issues.filter((i) => i.open > 0);
              const decided = row.issues.reduce((sum, i) => sum + i.ruleResolved, 0);
              return (
                <tr key={`${row.year}|${row.channelStatus}|${row.paymentState}`}>
                  <td>{row.year}</td>
                  <td>{row.channelStatus}</td>
                  <td>{PAYMENT_LABELS[row.paymentState]}</td>
                  <td className="align-right num">
                    {row.orders}
                    {row.withRefunds > 0 && <div className="small muted">{row.withRefunds} refunded</div>}
                  </td>
                  <td className="align-right num">{usd(row.totalMinor)}</td>
                  <td className="align-right num">
                    {usd(row.paidMinor)}
                    {row.refundedMinor > 0 && <div className="small muted">−{usd(row.refundedMinor)} refunds</div>}
                  </td>
                  <td className="align-right num">
                    {usd(row.recognizedMinor)}
                    <div className="small muted">{row.countable} orders</div>
                  </td>
                  <td>
                    {open.map((issue) => (
                      <div key={issue.ruleId}>
                        <span className={`badge ${rulesById.get(issue.ruleId)?.severity === 'conflict' ? 'error' : 'warn'}`}>
                          {rulesById.get(issue.ruleId)?.label ?? issue.ruleId} × {issue.open}
                        </span>
                      </div>
                    ))}
                    {decided > 0 && <div className="small muted">{decided} decided by rules</div>}
                    {open.length === 0 && decided === 0 && <span className="muted small">none</span>}
                  </td>
                  <td>
                    {open.length > 0 && (
                      <button className="link" onClick={() => setDeciding(row)}>
                        Decide…
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {deciding && (
        <RuleForm
          key={`${deciding.year}|${deciding.channelStatus}|${deciding.paymentState}`}
          channel={channel}
          row={deciding}
          rules={profile.data?.rules ?? []}
          onClose={() => setDeciding(null)}
        />
      )}

      {channel.capabilities.recognitionBasis === 'payments' && <Reconciliation channel={channel} />}
    </>
  );
}

/** The treatment most cells like this one are meant to get; the admin can change it. */
function suggestedTreatment(row: ProfileRow): RuleTreatment {
  if (row.paymentState === 'none' && row.issues.some((i) => i.ruleId === 'completed_no_payment')) return 'count_order_total';
  if (row.issues.some((i) => i.ruleId === 'cancelled_with_payment')) return 'exclude';
  return 'count_payments';
}

function RuleForm({
  channel,
  row,
  rules,
  onClose,
}: {
  channel: SalesChannelStatus;
  row: ProfileRow;
  rules: ReviewRuleInfo[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const openRuleIds = row.issues.filter((i) => i.open > 0).map((i) => i.ruleId);
  const [ruleIds, setRuleIds] = useState<string[]>(openRuleIds);
  const [placedFrom, setPlacedFrom] = useState(`${row.year}-01-01`);
  const [placedTo, setPlacedTo] = useState(`${row.year}-12-31`);
  const [treatment, setTreatment] = useState<RuleTreatment>(suggestedTreatment(row));
  const [name, setName] = useState(
    `${channel.name} ${row.year}, ${row.channelStatus}, ${PAYMENT_LABELS[row.paymentState].toLowerCase()}`,
  );
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const match: ResolutionMatch = {
    ruleIds,
    channelIds: [channel.channelId],
    ...(placedFrom ? { placedFrom } : {}),
    ...(placedTo ? { placedTo } : {}),
    channelStatuses: [row.channelStatus],
    paymentStates: [row.paymentState],
  };

  const preview = useQuery({
    queryKey: ['sales-rule-preview', match],
    queryFn: () => salesApi.previewRule(match),
    enabled: ruleIds.length > 0,
  });

  const create = useMutation({
    mutationFn: () => salesApi.createRule({ name, match, treatment, note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales-profile'] });
      void queryClient.invalidateQueries({ queryKey: ['sales-rules'] });
      void queryClient.invalidateQueries({ queryKey: ['sales-status'] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  const labelOf = (id: string) => rules.find((r) => r.id === id)?.label ?? id;

  return (
    <div className="card" style={{ marginTop: 12, maxWidth: 680 }}>
      <h4 style={{ marginTop: 0 }}>Decide with a saved rule</h4>
      <p className="small muted">
        {channel.name} orders with status <strong>{row.channelStatus}</strong> and{' '}
        <strong>{PAYMENT_LABELS[row.paymentState].toLowerCase()}</strong>. The rule also decides matching orders imported
        later, until it is revoked. A decision someone made on a single order is never overridden.
      </p>
      {error && <div className="alert error">{error}</div>}

      <div className="field">
        <label>Questions it answers</label>
        {openRuleIds.map((id) => (
          <div key={id}>
            <label>
              <input
                type="checkbox"
                checked={ruleIds.includes(id)}
                onChange={(e) => setRuleIds(e.target.checked ? [...ruleIds, id] : ruleIds.filter((r) => r !== id))}
              />{' '}
              {labelOf(id)}
            </label>
          </div>
        ))}
      </div>

      <div className="field-row">
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="rule-from">Placed from</label>
          <input id="rule-from" type="date" value={placedFrom} onChange={(e) => setPlacedFrom(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="rule-to">Placed through</label>
          <input id="rule-to" type="date" value={placedTo} onChange={(e) => setPlacedTo(e.target.value)} />
        </div>
      </div>

      <div className="field">
        <label htmlFor="rule-treatment">Count these orders as</label>
        <select id="rule-treatment" value={treatment} onChange={(e) => setTreatment(e.target.value as RuleTreatment)}>
          {(Object.keys(TREATMENT_LABELS) as RuleTreatment[]).map((t) => (
            <option key={t} value={t}>
              {TREATMENT_LABELS[t]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="rule-name">Name</label>
        <input id="rule-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="rule-note">Why</label>
        <textarea
          id="rule-note"
          rows={2}
          style={{ width: '100%', boxSizing: 'border-box', font: 'inherit' }}
          value={note}
          placeholder="e.g. Before 2020 phone orders were marked completed without entering the payment"
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <div className="small" style={{ margin: '8px 0' }}>
        {ruleIds.length === 0 ? (
          <span className="muted">Pick at least one question.</span>
        ) : preview.data ? (
          <>
            Decides <strong>{preview.data.issues}</strong> open issue{preview.data.issues === 1 ? '' : 's'} on{' '}
            {preview.data.orders} order{preview.data.orders === 1 ? '' : 's'} ({usd(preview.data.amountAtRiskMinor)} in
            question).
            {preview.data.decidedByPerson > 0 && ` ${preview.data.decidedByPerson} already decided by a person stay as they are.`}
            {preview.data.decidedByOtherRule > 0 && ` ${preview.data.decidedByOtherRule} stay with an earlier rule.`}
          </>
        ) : (
          <span className="muted">Counting…</span>
        )}
      </div>

      <button
        className="primary"
        disabled={create.isPending || ruleIds.length === 0 || note.trim() === '' || name.trim() === ''}
        onClick={() => create.mutate()}
      >
        {create.isPending ? 'Saving…' : 'Save rule'}
      </button>{' '}
      <button className="link" onClick={onClose}>
        Cancel
      </button>
    </div>
  );
}

function describeMatch(match: ResolutionMatch, channels: SalesChannelStatus[]): string {
  const parts: string[] = [];
  if (match.channelIds?.length) {
    parts.push(match.channelIds.map((id) => channels.find((c) => c.channelId === id)?.name ?? id).join(', '));
  }
  if (match.placedFrom || match.placedTo) parts.push(`placed ${match.placedFrom ?? '…'} to ${match.placedTo ?? '…'}`);
  if (match.channelStatuses?.length) parts.push(`status ${match.channelStatuses.join('/')}`);
  if (match.paymentStates?.length) parts.push(match.paymentStates.map((p) => PAYMENT_LABELS[p].toLowerCase()).join('/'));
  return parts.join(' · ');
}

function ResolutionRules({ channels }: { channels: SalesChannelStatus[] }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['sales-rules'], queryFn: salesApi.resolutionRules });
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const revoke = useMutation({
    mutationFn: (rule: ResolutionRule) => salesApi.revokeRule(rule.id),
    onSuccess: (result) => {
      setMessage({ ok: true, text: `Revoked; ${result.reopened} issue${result.reopened === 1 ? '' : 's'} re-opened for review` });
      void queryClient.invalidateQueries({ queryKey: ['sales-rules'] });
      void queryClient.invalidateQueries({ queryKey: ['sales-profile'] });
      void queryClient.invalidateQueries({ queryKey: ['sales-status'] });
    },
    onError: (err: Error) => setMessage({ ok: false, text: err.message }),
  });

  const rules = data?.rules ?? [];
  if (rules.length === 0) return null;

  return (
    <>
      <h4>Saved rules</h4>
      {message && (
        <div className={`alert ${message.ok ? 'ok' : 'error'}`} onClick={() => setMessage(null)}>
          {message.text}
        </div>
      )}
      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Counts as</th>
              <th className="align-right num">Deciding</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id} className={rule.enabled ? '' : 'muted'}>
                <td>
                  {rule.name}
                  <div className="small muted">{describeMatch(rule.match, channels)}</div>
                  <div className="small muted">“{rule.note}”</div>
                </td>
                <td className="small">{TREATMENT_LABELS[rule.treatment]}</td>
                <td className="align-right num">{rule.enabled ? rule.applied : '—'}</td>
                <td className="small muted">
                  {new Date(rule.createdAt).toLocaleDateString()}
                  {rule.createdById && <div>{rule.createdById}</div>}
                </td>
                <td>
                  {rule.enabled ? (
                    <button
                      className="link"
                      disabled={revoke.isPending}
                      onClick={() => {
                        if (window.confirm(`Revoke "${rule.name}"? Every issue it decided goes back to review.`)) {
                          revoke.mutate(rule);
                        }
                      }}
                    >
                      Revoke
                    </button>
                  ) : (
                    <span className="badge">revoked</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Reconciliation({ channel }: { channel: SalesChannelStatus }) {
  const [showAll, setShowAll] = useState(false);
  const check = useMutation({ mutationFn: () => salesApi.reconcile(channel.channelId) });
  const months: ReconciliationRow[] = check.data?.months ?? [];
  const differing = months.filter((m) => !m.matches);
  const unexplained = differing.filter((m) => m.coverage >= 1);
  const shown = showAll ? months : differing;

  return (
    <div style={{ marginTop: 16 }}>
      <h4>Compare with the site</h4>
      <p className="small muted">
        Asks {channel.name} for its own monthly order counts, totals and payments, and compares them with what was
        imported. A fully imported month that differs means orders are missing or out of date.
      </p>
      <button disabled={check.isPending} onClick={() => check.mutate()}>
        {check.isPending ? 'Comparing…' : 'Compare now'}
      </button>
      {check.error && <div className="alert error">{(check.error as Error).message}</div>}
      {check.data && (
        <>
          <div className={`alert ${unexplained.length > 0 ? 'warn' : 'ok'}`}>
            {months.length} months checked: {months.length - differing.length} match
            {differing.length > 0 &&
              `, ${differing.length} differ${unexplained.length < differing.length ? ` (${differing.length - unexplained.length} not fully imported yet)` : ''}`}
            .
          </div>
          <label className="small">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Show every month
          </label>
          {shown.length > 0 && (
            <div className="table-wrap">
              <table className="grid">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="align-right num">Orders (site / imported)</th>
                    <th className="align-right num">Order totals</th>
                    <th className="align-right num">Received</th>
                    <th className="align-right num">Refunded</th>
                    <th>Imported</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((m) => (
                    <tr key={m.month}>
                      <td>
                        {m.month} {!m.matches && <span className={`badge ${m.coverage >= 1 ? 'error' : 'warn'}`}>differs</span>}
                      </td>
                      <td className="align-right num">
                        {m.site.orders} / {m.imported.orders}
                      </td>
                      <td className="align-right num">
                        {usdExact(m.site.orderTotalMinor)} / {usdExact(m.imported.orderTotalMinor)}
                      </td>
                      <td className="align-right num">
                        {usdExact(m.site.receivedMinor)} / {usdExact(m.imported.receivedMinor)}
                      </td>
                      <td className="align-right num">
                        {usdExact(m.site.refundedMinor)} / {usdExact(m.imported.refundedMinor)}
                      </td>
                      <td className="small muted">{Math.round(m.coverage * 100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
